import { makeAutoObservable, runInAction } from "mobx";

import { AppRegistry } from "Apps/AppRegistry";
import { Constants } from "Shared/Constants";
import { Dashboard } from "Apps/Dashboard/DashboardApp.setting";
import { TRootStore } from "./RootStore";
import { TeamAppEnvs } from "Apps/AppRegistryEnvs";
import { TeamAppModel } from "Apps/TeamAppModel";
import axios from "axios";

declare const StorybookSettings: AppSettings | undefined;

export type AppSettings = Record<
    | "NODE_ENV"
    | "m365appVersion"
    | "appInsightsLog"
    | "applicationInsightsConnectionString"
    | "ospServiceApiUrl"
    | "identityServiceApiUrl"
    | "innerRingIdentityServiceApiUrl"
    | "elipServiceApiUrl"
    | "aadUrl"
    | "clientIdInMicrosoft"
    | "clientIdInTorus"
    | "microsoftTenantId"
    | "torusTenantId"
    | "icmUrl"
    | "genevaUrl"
    | "validCloudInstances"
    | "serviceTreeUrl"
    | "skypeAdoUrl"
    | "engMsUrl"
    | "teamsEndPoint"
    | "teamsChannelEndPoint"
    | "teamsChannel"
    | "applicationInsightsUrl"
    | "engHubUrl"
    | "yubiKey5UsageUrl"
    | "webDMSProxyScriptRequestUrl"
    | "scpInfraTenantMonitoringGrafanaUrl"
    | "raveUrl"
    | "yubiSetUpURL"
    | "subscribeMPABotURL"
    | "mpaBotTroubleshootURL"
    | "openMPABotURL"
    | "requestYubiKeyURL"
    | "clearanceDocsURL"
    | "fteBackgroundCheckURL"
    | "fismaTrainingURL"
    | "privacyURL"
    | "sipUsageURL"
    | "privacyContactsURL"
    | "onlineServiceTermsURL"
    | "dataHandlingURL"
    | "iridiasIncidentUrl"
    | "opsGenieSignalRUrl"
    | "opsGenieOBOAgents"
    | "changeManagementWikiUrl"
    | "azureDevOpsUrl"
    | "TorusWebDMSAuthTenantId"
    | "TorusWebDMSAuthClientId"
    | "TorusWebDMSAuthScope"
    | "TorusWebDMSEndpointFormatter"
    | "TorusEnvEndpointFormatter"
    | "M365OpsCenterVF_EnvironmentList"
    | "enableUMSMyEligibilities"
    | "enableUMSManageEligibilities"
    | "enableUMSDataEligibilities"
    | "enableUMSScenarios"
    | "enableUMSAccessApprovals"
    | "enableUMSClearances"
    | "enableUMSInnerRing"
    | "enableUMSBusinessJustification"
    | "ospCoreDomainApiUrl",
    string | undefined
>;

type TeamAppModelLive = TeamAppModel & { loaded?: boolean };

export const AppSettingStore = (root: TRootStore) =>
    makeAutoObservable({
        settings: {} as AppSettings,
        allApps: [] as TeamAppModelLive[],
        get appModels(): TeamAppModel[] {
            return this.allApps.filter((app) => this.checkEnv(app.envs) && this.checkRole(app.role) && this.checkFlighting(app.flighting));
        },
        currentApp: Dashboard as TeamAppModelLive,
        async init() {
            if (process.env.NODE_ENV === "development") {
                this.settings = typeof StorybookSettings !== "undefined" ? StorybookSettings : (process.env as AppSettings);
            } else {
                const response = await axios.get<AppSettings>("/api/core/settingv2");
                runInAction(() => {
                    this.settings = response.data;
                });
            }

            if (this.settings.ospCoreDomainApiUrl) {
                const hostname = window.location.hostname;
                if (hostname.endsWith(".core.dev.microsoft") || hostname.endsWith(".core.microsoft")) {
                    runInAction(() => {
                        this.settings = { ...this.settings, ospServiceApiUrl: this.settings.ospCoreDomainApiUrl };
                    });
                }
            }

            this.registerApps();
        },
        registerApps() {
            const settings = AppRegistry;
            Object.entries(settings).forEach(([key, app]) => {
                const env = TeamAppEnvs.get(key as keyof typeof AppRegistry);

                const teamApp: TeamAppModelLive = {
                    id: key,
                    ...app,
                    envs: env ?? Constants.ppeOnlyEnvs,
                    loaded: false
                };

                this.allApps.push(teamApp);
            });
        },
        setCurrentApp(current: TeamAppModel) {
            this.currentApp = current;
        },
        checkEnv(envs: string[]) {
            return envs.some((env) => this.settings.NODE_ENV && env.toLowerCase() === this.settings.NODE_ENV.toLowerCase());
        },
        checkRole(role: string) {
            if (role === "*") {
                return true;
            }
            const appRoles = role.split(";");
            const userRoles = root.MyStore.profile.Roles ?? [];
            return appRoles.some((appRole) => userRoles.includes(appRole));
        },
        checkFlighting(flighting: string[] | undefined) {
            if (!flighting || flighting.length === 0) {
                return true;
            }
            const userFlighting = root.MyStore.flighting;
            return flighting.some((appFlighting) => userFlighting.includes(appFlighting.toLowerCase()));
        }
    });
